import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flagged = params.get("suspended") === "1" || sessionStorage.getItem("tj_suspended_toast") === "1";
    if (flagged) {
      toast.error(t("auth.suspended_toast"));
      try { sessionStorage.removeItem("tj_suspended_toast"); } catch (_) { /* ignore */ }
    }
  }, [location.search, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Suspended-account login: show a dedicated inline message + toast.
      if (err.response?.status === 401 && detail?.code === "SUSPENDED") {
        toast.error(t("auth.suspended_toast"));
        setError(t("auth.suspended_toast"));
      } else {
        setError(formatApiErrorDetail(detail) || t("common.error_generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="login-page">
      <Toaster richColors position="top-right" />
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900" data-testid="login-brand-link">
          <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
          <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
        </Link>
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t("auth.login_title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("auth.login_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t("auth.email")}
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t("auth.password")}
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                data-testid="login-password-input"
              />
              <div className="mt-1.5 text-right">
                <Link to="/password-reset" className="text-xs font-medium text-indigo-700 hover:text-indigo-800" data-testid="login-forgot-link">
                  {t("password_reset.forgot_link")}
                </Link>
              </div>
            </div>

            {error && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {submitting ? t("common.loading") : t("auth.submit_login")}
            </button>
          </form>

          <div className="mt-6 space-y-1 text-sm text-slate-600">
            <p>{t("auth.no_account")}</p>
            <div className="flex flex-col gap-1">
              <Link
                to="/signup/employer"
                className="font-medium text-indigo-700 hover:text-indigo-800"
                data-testid="link-signup-employer"
              >
                {t("auth.signup_employer_link")}
              </Link>
              <Link
                to="/signup/agency"
                className="font-medium text-indigo-700 hover:text-indigo-800"
                data-testid="link-signup-agency"
              >
                {t("auth.signup_agency_link")}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
